import { Icon } from "@/components/Icon";

/**
 * MetricCard — the 4-up dashboard stat tile.
 *
 * Originally defined inline on the dashboard route; promoted here so the
 * Profile and Person-details pages can share the same shell instead of
 * duplicating the `.metric` markup.
 *
 * `spark` is rendered as the small bar-chart that lives on the dashboard's
 * "Completed this month" tile. `footer` is the bottom strap (with tone
 * colour + optional icon) used on dashboard tiles; profile tiles use it
 * for the "all time" / "on track" sub-labels.
 */
export type MetricCardProps = {
  label: string;
  value: number | string;
  spark?: number[];
  footer?: {
    text: string;
    tone?: "up" | "warn" | "flat";
    icon?: "clock" | "arrow" | "flag" | "check";
  };
};

export function MetricCard({ label, value, spark, footer }: MetricCardProps) {
  return (
    <div className="metric" data-testid="metric-card">
      <div className="m-lab">{label}</div>
      <div className="m-val">{value}</div>
      {footer ? (
        <div className={`m-foot${footer.tone ? ` ${footer.tone}` : ""}`}>
          {footer.icon ? <Icon name={footer.icon} size={12} /> : null}
          {footer.text}
        </div>
      ) : null}
      {spark && spark.length > 0 ? (
        <div className="m-spark" aria-hidden="true">
          {spark.map((n, i) => {
            const max = Math.max(...spark, 1);
            const pct = Math.round((n / max) * 100);
            const hi = n === max && n > 0;
            return (
              <i
                key={i}
                className={hi ? "hi" : undefined}
                style={{ height: `${Math.max(8, pct)}%` }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
